// Ported from eb-ui `src/legacy/CollectionEvents/CollectionEvents.tsx` — the
// "Events in this collection" section (upcoming/past tabs, the paginated
// "Show more events" read, the destination upgrade, the no-upcoming-events
// empty state).
//
// Mapping:
//   - `@eventbrite/event-renderer` (`EventClient`/`EventClientProvider` —
//     ABSENT from the sanctioned 1.5.2) → DROPPED (a module-global fetcher;
//     nothing in this tree reads its context).
//   - `@eventbrite/event-renderer` `getEventsById` + `sdkRequest` → the local
//     BFF-routed `fetchCollectionEventsPage` + `updateEventData` in
//     `../lib/data` (same origin→destination upgrade behaviour: page 0 re-
//     fetches destination to upgrade the SSR seed; page >= 2 does the BFF
//     pagination read then the destination upgrade).
//   - `@eventbrite/eds-tabs` → `../lib/Tabs` (state-machine Tabs with
//     `shouldKeepContentMounted` + `onTabChange` — the upcoming/past tabs keep
//     their content mounted; `onTabChange` fires the GA track).
//   - `@eventbrite/eds-event-card` `LoadingCards` → `../lib/eds/LoadingCards`.
//   - `@eventbrite/legacy-infra.context-gen` `CoreEnv` → the local `Env`.
//   - `@eventbrite/legacy-infra.i18n` → `@/lib/gettext`; icons →
//     `../lib/iconography`.
//   - `translateServerErrors` → a local try/catch (the GA error surface is a
//     ghost).
import { Button } from '@eventbrite/legacy-eds.button';
import { Divider } from '@eventbrite/legacy-eds.divider';
import {
    EventCardShareAction,
    getShareActionProps,
} from '../lib/eventRenderer';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { FollowOrganizer } from '../components/FollowOrganizerButton/FollowOrganizerButton';
import { AnalyticsContext, ApplicationContext } from '../context';
import { CreatorCollectionsVerticalEventCard } from '../CreatorCollectionsVerticalEventCard';
import {
    AffiliateCodes,
    CollectionEventCard,
    CollectionServerData,
    EventListProps,
    EventOriginType,
    Pagination,
} from '@/types';
import { AnalyticsAction, AnalyticsCategory, trackEvent } from '../_shared';
import {
    adjustRepeatingEventTimes,
    sortEventsByStartDate,
    transformCollectionEvent,
} from '../_shared/transform';
import { LoadingCards } from '../lib/eds/LoadingCards';
import { Tabs } from '../lib/Tabs';
import { Icon } from '../lib/iconography';
import { CalendarFill } from '../lib/iconography';
import type { Env } from '../types';
import { gettext } from '@/lib/gettext';
import {
    fetchCollectionEventsPage,
    PAST_EVENTS_PAGE_SIZE,
    updateEventData,
} from '../lib/data';

const creatorCollectionsUtmOptions = {
    'utm-campaign': 'social',
    'utm-content': 'attendeeshare',
    'utm-medium': 'discovery',
    'utm-term': 'creator-collections',
    'utm-share-source': 'creator-collections',
};

export const PAST_EVENTS_SHOWED_WHEN_ONLY_PAST_EVENTS = 3;
export const EVENTS_IN_COLLECTION_PAGE_SIZE = 24;

enum Tab {
    Upcoming = 0,
    Past = 1,
}
interface Props {
    env: Env;
}

export const CollectionEvents: React.FC<Props> = ({ env }) => {
    const context = useContext(ApplicationContext) as CollectionServerData;
    const { collectionId } = useContext(AnalyticsContext);
    const { past, upcoming } = context.events_in_collection;
    const organizerName = context.collection.organizer?.name;
    const [pastEvents, upcomingEvents] = [past, upcoming].map(
        (sublist) =>
            (sublist?.events || []).map((event: EventOriginType) =>
                transformCollectionEvent(event),
            ) || [],
    );

    const hasPastAndUpcomingEvents =
        pastEvents?.length > 0 && upcomingEvents?.length > 0;
    const hasOnlyPastEvents =
        pastEvents?.length > 0 && upcomingEvents?.length === 0;
    const hasOnlyUpcomingEvents =
        pastEvents?.length === 0 && upcomingEvents?.length > 0;
    const notPastNorUpcomingEvents =
        pastEvents?.length === 0 && upcomingEvents?.length === 0;
    return (
        <div
            className="eds-l-mar-bot-10"
            data-testid="collection-events-section"
        >
            <h2 className="eds-text-hm eds-l-mar-bot-6 cc-heading">
                {gettext('Events in this collection')}
            </h2>
            {hasPastAndUpcomingEvents && (
                <Tabs
                    role="tablist"
                    shouldKeepContentMounted={true}
                    onTabChange={(tab: Tab) => {
                        trackEvent({
                            action: AnalyticsAction.EventsTabClick,
                            category: AnalyticsCategory.Listing,
                            label: tab === Tab.Upcoming ? 'upcoming' : 'past',
                        });
                    }}
                    items={[
                        {
                            displayName: `${gettext('Upcoming')} (${
                                upcoming.pagination?.object_count || 0
                            })`,
                            value: Tab.Upcoming,
                            content: (
                                <EventList
                                    collectionId={collectionId}
                                    events={upcomingEvents}
                                    pagination={upcoming.pagination}
                                    eventType={EventType.Upcoming}
                                    initialMaxEventsToShow={
                                        EVENTS_IN_COLLECTION_PAGE_SIZE
                                    }
                                    env={env}
                                />
                            ),
                        },
                        {
                            displayName: `${gettext('Past')} ${
                                past.pagination?.object_count ===
                                pastEvents.length
                                    ? `(${
                                          past.pagination?.object_count || 0
                                      })`
                                    : ''
                            }`,
                            value: Tab.Past,
                            content: (
                                <EventList
                                    collectionId={collectionId}
                                    events={pastEvents}
                                    pagination={past.pagination}
                                    eventType={EventType.Past}
                                    initialMaxEventsToShow={
                                        EVENTS_IN_COLLECTION_PAGE_SIZE
                                    }
                                    env={env}
                                />
                            ),
                        },
                    ]}
                />
            )}
            {(hasOnlyPastEvents || notPastNorUpcomingEvents) && (
                <div className="cc-no-upcoming-events">
                    <Icon
                        type={<CalendarFill />}
                        size="medium"
                        color="ui-800"
                    />

                    <h3 className="eds-text-bl eds-l-mar-top-5">
                        {gettext('No upcoming events')}
                    </h3>
                    {organizerName && (
                        <>
                            <p className="eds-text-color--grey-600 eds-l-mar-top-1">
                                {gettext(
                                    'Follow %(organizerName)s to never miss a moment',
                                    {
                                        organizerName,
                                    },
                                )}
                            </p>
                            <div className="eds-l-mar-top-5">
                                <FollowOrganizer />
                            </div>
                        </>
                    )}
                </div>
            )}
            {hasOnlyPastEvents && (
                <>
                    <div className="eds-l-mar-top-10 eds-l-mar-bot-10">
                        <Divider color="grey-400" />
                    </div>
                    <h3 className="eds-text-bl eds-l-mar-bot-8">
                        {gettext('Here’s what you may have missed')}
                    </h3>
                    <EventList
                        collectionId={collectionId}
                        events={pastEvents}
                        pagination={past.pagination}
                        eventType={EventType.Past}
                        initialMaxEventsToShow={
                            PAST_EVENTS_SHOWED_WHEN_ONLY_PAST_EVENTS
                        }
                        env={env}
                    />
                </>
            )}
            {hasOnlyUpcomingEvents && (
                <EventList
                    collectionId={collectionId}
                    events={upcomingEvents}
                    pagination={upcoming.pagination}
                    eventType={EventType.Upcoming}
                    initialMaxEventsToShow={EVENTS_IN_COLLECTION_PAGE_SIZE}
                    env={env}
                />
            )}
        </div>
    );
};

interface EventListDisplayProps {
    events: CollectionEventCard[];
    collectionId: string;
    pagination: Pagination;
    eventType: EventType;
    initialMaxEventsToShow: number;
    env: Env;
}

enum EventType {
    Past = 'past',
    Upcoming = 'current_future',
}

interface EventListItemProps {
    event: CollectionEventCard;
    env: Env;
    eventType: EventType;
}

const EvenListItem: React.FC<EventListItemProps> = ({
    event,
    env,
    eventType,
}) => {
    // BUGBOT (High): the card was hardcoded `isAuthenticated={true}` — treating
    // a GUEST as logged-in breaks save/auth affordances on the main event list
    // (CardsCarousel already used the real viewer auth). Read the actual auth
    // state from the shared ApplicationContext (the reconstructed serverData).
    const appContext = useContext(ApplicationContext) as
        | CollectionServerData
        | null;
    const isAuthenticated = appContext?.user?.isAuthenticated ?? false;

    return (
        <li className="cc-card-list__item">
            <CreatorCollectionsVerticalEventCard
                event={event}
                affCode={AffiliateCodes.EventsInOrganizerCollection}
                isAuthenticated={isAuthenticated}
                locale={env?.localeInfo.locale || 'en_US'}
                statsigLocationString=""
                moreActions={
                    event &&
                    eventType != EventType.Past && (
                        <EventCardShareAction
                            {...getShareActionProps({
                                name: event.name,
                                id: event.id,
                                url: event.url,
                                affCode:
                                    AffiliateCodes.EventsInOrganizerCollection,
                                utmOptions: creatorCollectionsUtmOptions,
                            })}
                        />
                    )
                }
            />
        </li>
    );
};
// TODO: Add types to argument object.
const EventList: React.FC<EventListDisplayProps> = ({
    events: initialEvents,
    collectionId,
    pagination,
    eventType,
    initialMaxEventsToShow,
    env,
}) => {
    const {
        events,
        numberOfEventsToShow,
        hasMoreEvents,
        loadNextPage,
        isLoadingMoreEvents,
    } = useCollectionPaginatedEvents({
        events: initialEvents,
        collectionId,
        eventType,
        // BUGBOT (Medium): the Show-more gate must use the tab's own page
        // size. eb-ui gated past at 24 while seeding 18, so in the TABBED
        // layout collections with 19-24 past events never showed the button
        // (those events were unreachable there too). With past pagination
        // uniformly 18 (see PAST_EVENTS_PAGE_SIZE), gate at 18 so those
        // events load. In the PAST-ONLY layout eb-ui intentionally gates at
        // 3 (initialMax) — a click with has_more_items=false just reveals
        // the already-seeded cards 4..18 — so take the smaller of the two
        // thresholds for past lists.
        hasMoreEvents:
            (pagination.object_count || 0) >
            (eventType === EventType.Past
                ? Math.min(initialMaxEventsToShow, PAST_EVENTS_PAGE_SIZE)
                : initialMaxEventsToShow),
        hasPaginatedEvents: pagination.has_more_items,
        initialMaxEventsToShow,
    });
    return (
        <>
            <ul className="cc-card-list">
                {events
                    .slice(0, numberOfEventsToShow)
                    .map((event: CollectionEventCard) => (
                        <EvenListItem
                            key={event.id}
                            event={event}
                            env={env}
                            eventType={eventType}
                        />
                    ))}
            </ul>

            {hasMoreEvents && (
                <>
                    {isLoadingMoreEvents ? (
                        <div className="cc-loading-cards">
                            <LoadingCards style="grid" cardCount={3} />
                        </div>
                    ) : (
                        <div className="cc-card-list-show-more-events">
                            <Button
                                onClick={() => {
                                    loadNextPage();
                                    trackEvent({
                                        action:
                                            AnalyticsAction.EventsShowMore,
                                        category: AnalyticsCategory.Listing,
                                        label:
                                            eventType === EventType.Upcoming
                                                ? 'upcoming'
                                                : 'past',
                                    });
                                }}
                            >
                                {gettext('Show more events')}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

interface collectionEventsPaginationProps {
    collectionId: string;
    events: CollectionEventCard[];
    eventType: EventType;
    hasPaginatedEvents: boolean;
    hasMoreEvents: boolean;
    initialMaxEventsToShow: number;
}

// BUGBOT (Medium): the destination upgrade can legitimately return FEWER
// events than the requested page (an unpublished/expired id is simply absent
// from the destination response). Merge by EVENT ID so a missing id falls back
// to its ORIGIN card instead of being dropped, and append by id so a partially
// upgraded page can't duplicate rows.
const byId = (cards: CollectionEventCard[]): Map<string, CollectionEventCard> =>
    new Map(cards.map((c) => [c.id, c]));

const mergeUpgradeWithOrigin = (
    origin: CollectionEventCard[],
    upgraded: CollectionEventCard[],
): CollectionEventCard[] =>
    origin.map((card) => byId(upgraded).get(card.id) ?? card);

const appendUnique = (
    base: CollectionEventCard[],
    incoming: CollectionEventCard[],
): CollectionEventCard[] => {
    const seen = new Set(base.map((c) => c.id));
    return [...base, ...incoming.filter((c) => !seen.has(c.id))];
};

function useCollectionPaginatedEvents({
    collectionId,
    events: initialEvents,
    eventType,
    hasMoreEvents: initialHasMoreEvents,
    hasPaginatedEvents,
    initialMaxEventsToShow,
}: collectionEventsPaginationProps) {
    const [events, setEvents] = useState(initialEvents);
    const [page, setPage] = useState(0);
    const [hasMoreEvents, setHasMoreEvents] = useState(initialHasMoreEvents);
    const [isLoadingMoreEvents, setIsLoadingMoreEvents] = useState(false);
    // BUGBOT (Medium): mirror the loading flag in a ref so `loadNextPage` can
    // guard synchronously. The state flip happens INSIDE the `page > 0` effect,
    // so between the click and the effect there is a window where a second
    // click would advance `page` again (and skip a Core page) — the state
    // value alone is a stale-closure read at that point.
    const isLoadingMoreRef = useRef(false);
    const markLoadingMore = (loading: boolean) => {
        isLoadingMoreRef.current = loading;
        setIsLoadingMoreEvents(loading);
    };
    useEffect(() => {
        if (page > 0) {
            const nextPage = page + 1;

            if (hasPaginatedEvents) {
                markLoadingMore(true);
                fetchCollectionEventsPage(
                    collectionId,
                    eventType as 'current_future' | 'past',
                    nextPage,
                    eventType === EventType.Past ? PAST_EVENTS_PAGE_SIZE : undefined,
                )
                    .then((newUpcomingEvents: EventListProps) => {
                        const originCards = sortEventsByStartDate(
                            adjustRepeatingEventTimes(
                                newUpcomingEvents.events.map(
                                    transformCollectionEvent,
                                ),
                            ),
                            eventType === EventType.Upcoming,
                        );
                        const eventIdList = originCards.map(
                            (event) => event.id,
                        );
                        updateEventData(eventIdList).then(
                            (newUpcomingEventsData) => {
                                const upgradedCards = sortEventsByStartDate(
                                    // The BFF returns destination `FormattedEvent[]`
                                    // (a structural superset of the card shape the
                                    // local sort/adjust helpers operate on).
                                    adjustRepeatingEventTimes(
                                        newUpcomingEventsData.events as CollectionEventCard[],
                                    ),
                                    eventType === EventType.Upcoming,
                                );
                                // BUGBOT (Medium): the upgrade may be PARTIAL
                                // (an expired/unpublished id is omitted from the
                                // destination response) — merge by id so those
                                // ids keep their ORIGIN cards rather than being
                                // dropped, then append by id (no duplicates).
                                const pageCards = mergeUpgradeWithOrigin(
                                    originCards,
                                    upgradedCards,
                                );
                                setEvents((prev) =>
                                    appendUnique(prev, pageCards),
                                );
                                setHasMoreEvents(
                                    newUpcomingEvents.pagination
                                        .has_more_items,
                                );
                                markLoadingMore(false);
                            },
                            // BUGBOT (High): the origin→destination upgrade is
                            // display-only enrichment. When it FAILS (transient
                            // Core/BFF error) fall back to the ORIGIN events the
                            // pagination read JUST returned — the new page is
                            // still appended, and the spinner + `has_more_items`
                            // still unwind.
                            (error: unknown) => {
                                console.error(
                                    'collection events destination upgrade failed; falling back to origin events',
                                    error,
                                );
                                setEvents((prev) =>
                                    appendUnique(prev, originCards),
                                );
                                setHasMoreEvents(
                                    newUpcomingEvents.pagination
                                        .has_more_items,
                                );
                                markLoadingMore(false);
                            },
                        );
                    })
                    .catch((error: unknown) => {
                        console.error(
                            'collection events page fetch failed',
                            error,
                        );
                        markLoadingMore(false);
                    });
            } else {
                //display past hidden ones
                setHasMoreEvents(false);
                markLoadingMore(false);
            }
        } else {
            const eventIdList = events.map((event) => event.id);
            updateEventData(eventIdList)
                .then((newUpcomingEventsData) => {
                    if (newUpcomingEventsData?.events?.length > 0) {
                        const upgradedCards = sortEventsByStartDate(
                            adjustRepeatingEventTimes(
                                newUpcomingEventsData.events as CollectionEventCard[],
                            ),
                            eventType === EventType.Upcoming,
                        );
                        // BUGBOT (Medium): the page-0 upgrade replaces ONLY the
                        // initial seed ids it actually upgraded, preserving any
                        // pages a fast "Show more" click already appended. The
                        // upgraded ids are the intersection of the (partial)
                        // upgrade and the CURRENT list — anything else (packed
                        // pages, tail) is kept untouched.
                        setEvents((prev) => {
                            const upgradedIds = new Set(
                                upgradedCards.map((c) => c.id),
                            );
                            const upgraded = mergeUpgradeWithOrigin(
                                prev.filter((c) => upgradedIds.has(c.id)),
                                upgradedCards,
                            );
                            const kept = prev.filter(
                                (c) => !upgradedIds.has(c.id),
                            );
                            const byOriginalIndex = new Map(
                                prev.map((c, i) => [c.id, i] as const),
                            );
                            return [...upgraded, ...kept].sort(
                                (a, b) =>
                                    (byOriginalIndex.get(a.id) ?? 0) -
                                    (byOriginalIndex.get(b.id) ?? 0),
                            );
                        });
                    }
                })
                .catch((error: unknown) => {
                    // page-0 destination upgrade is display-only enrichment —
                    // keep the (already-transformed) SSR seed, never crash.
                    console.error(
                        'initial collection events destination upgrade failed',
                        error,
                    );
                });
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, collectionId, eventType, setEvents]);

    const numberOfEventsToShow =
        page === 0
            ? initialMaxEventsToShow
            : initialMaxEventsToShow + page * EVENTS_IN_COLLECTION_PAGE_SIZE;

    return {
        events,
        // BUGBOT (Medium): functional update + loading guard. A double-click in
        // the window before the `page > 0` effect runs would otherwise advance
        // `page` twice (skipping a Core page). The ref flips TRUE synchronously
        // on the first click, so a second click is ignored until the effect
        // clears it.
        loadNextPage: () => {
            if (isLoadingMoreRef.current) {
                return;
            }
            setPage((p) => p + 1);
        },
        numberOfEventsToShow,
        hasMoreEvents,
        isLoadingMoreEvents,
    };
}
